/**
 * EVENTSFLO-ADD-CREW-PHOTO-UPLOAD-AND-CROP-V1
 * Square (1:1) crop dialog for crew profile photos.
 * Returns a JPEG Blob via onConfirm so the existing upload endpoint
 * (POST /api/staff/{id}/photo) keeps working unchanged.
 */
import { useCallback, useState } from "react";
import Cropper from "react-easy-crop";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import { Label } from "../ui/label";
import { Loader2 } from "lucide-react";

// Build a cropped JPEG Blob from a source image + pixel crop area
async function getCroppedBlob(imageSrc, pixelCrop, outputSize = 512) {
  const image = await new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = imageSrc;
  });

  const canvas = document.createElement("canvas");
  canvas.width = outputSize;
  canvas.height = outputSize;
  const ctx = canvas.getContext("2d");

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    outputSize,
    outputSize
  );

  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), "image/jpeg", 0.92);
  });
}

export default function PhotoCropDialog({
  open,
  imageSrc,
  fileName = "profile.jpg",
  onCancel,
  onConfirm,
}) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [busy, setBusy] = useState(false);

  const onCropComplete = useCallback((_area, areaPixels) => {
    setCroppedAreaPixels(areaPixels);
  }, []);

  const handleConfirm = async () => {
    if (!imageSrc || !croppedAreaPixels) return;
    setBusy(true);
    try {
      const blob = await getCroppedBlob(imageSrc, croppedAreaPixels, 512);
      if (!blob) throw new Error("Failed to generate cropped image");
      // Wrap as a File so backend filename handling stays consistent
      const baseName = (fileName || "profile").replace(/\.[^.]+$/, "");
      const cropped = new File([blob], `${baseName}-cropped.jpg`, {
        type: "image/jpeg",
      });
      await onConfirm(cropped);
    } finally {
      setBusy(false);
    }
  };

  const handleCancel = () => {
    if (busy) return;
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    onCancel?.();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleCancel()}>
      <DialogContent
        className="max-w-md"
        data-testid="photo-crop-dialog"
      >
        <DialogHeader>
          <DialogTitle>Crop profile photo</DialogTitle>
        </DialogHeader>

        <div
          className="relative w-full bg-black/60 rounded overflow-hidden"
          style={{ height: 320 }}
        >
          {imageSrc && (
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={1}
              cropShape="round"
              showGrid={false}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          )}
        </div>

        <div className="space-y-1 mt-2">
          <Label className="text-xs text-muted-foreground">Zoom</Label>
          <input
            type="range"
            min={1}
            max={4}
            step={0.05}
            value={zoom}
            onChange={(e) => setZoom(parseFloat(e.target.value))}
            className="w-full"
            data-testid="photo-crop-zoom"
          />
        </div>

        <DialogFooter className="mt-2">
          <Button
            type="button"
            variant="outline"
            onClick={handleCancel}
            disabled={busy}
            data-testid="photo-crop-cancel"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={busy || !croppedAreaPixels}
            data-testid="photo-crop-confirm"
          >
            {busy ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving…
              </>
            ) : (
              "Save crop"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
